/* Pulse collection page — the wall.
 *
 * Port of the interaction in storefront/app/routes/preview.collection.tsx and
 * components/pulse/collection/Filters.tsx. Vanilla, delegated off document so
 * it survives a theme-editor section reload without rebinding.
 *
 * What it does NOT do: filter. Filtering is a plain GET form now, which is
 * what Shopify's Search & Discovery facets are; the React state machine has no
 * equivalent here and should not grow one.
 *
 * Four jobs:
 *   1. one desktop facet panel open at a time, closing on outside click/Escape
 *   2. the mobile drawer: open, close, Escape, body scroll lock
 *   3. submit the desktop bar when a facet changes (the drawer waits for Show)
 *   4. play the social clips only while at least half on screen
 */
(function () {
  if (window.__pulse_collection) return;
  window.__pulse_collection = true;

  var SCROLL_LOCKED_BY = null;

  /* --- facet panels -------------------------------------------------------- */

  function panelFor(button) {
    var id = button.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function closePanels(except) {
    var buttons = document.querySelectorAll('.fx-bar [data-fx-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      if (buttons[i] === except) continue;
      var panel = panelFor(buttons[i]);
      buttons[i].setAttribute('aria-expanded', 'false');
      buttons[i].classList.remove('is-open');
      if (panel) panel.hidden = true;
    }
  }

  /* --- the drawer ---------------------------------------------------------- */

  /* Below 900px the drawer is an overlay bottom sheet and the page behind it
     must not scroll. From 900px up it is an inline column in the flow, where a
     scroll lock simply freezes the page (Andre, 2026-08-25). This matches the
     breakpoint in pulse-core.css.liquid -- change both together. */
  var OVERLAY = window.matchMedia('(max-width: 899px)');

  function lockScroll() {
    if (!OVERLAY.matches || SCROLL_LOCKED_BY !== null) return;
    SCROLL_LOCKED_BY = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  /* Never leave the body scroll-locked: the previous value is put back. */
  function unlockScroll() {
    if (SCROLL_LOCKED_BY === null) return;
    document.body.style.overflow = SCROLL_LOCKED_BY || '';
    SCROLL_LOCKED_BY = null;
  }

  /* PORTAL TO <body>, OVERLAY MODE ONLY (audit CSS-3, 2026-09-03). As a bottom
     sheet the drawer is position:fixed at z-index 80, but it renders inside
     <main>, which is a stacking context (`.pulse > * { z-index: 1 }`), so the
     fixed announcement bar (65) and nav (60) painted OVER its top edge and its
     scrim could not dim them. Moving it to <body> for the duration puts 80
     against 65 honestly. It goes HOME on close or on a resize past 900px,
     because from 900px up the drawer is an inline grid column that
     `.cx-body:has(.fx-drawer:not([hidden]))` places -- it must be back inside
     .cx-body, and before .cx-results, for that rule to see it. Same move the
     size drawer and the quick-add sheet make; every reference is by id or by
     document-wide query, so nothing here cares where the element lives. */
  function portalOut(drawer) {
    if (drawer.parentElement === document.body) return;
    if (!drawer.__fxHome) drawer.__fxHome = { parent: drawer.parentElement, next: drawer.nextElementSibling };
    document.body.appendChild(drawer);
  }
  function portalHome(drawer) {
    var h = drawer.__fxHome;
    if (!h || drawer.parentElement !== document.body) return;
    if (h.next && h.next.parentNode === h.parent) h.parent.insertBefore(drawer, h.next);
    else h.parent.insertBefore(drawer, h.parent.firstChild);
  }

  /* Opened as a sheet on a phone, then rotated or resized past the breakpoint:
     the overlay becomes an inline column and the lock would strand the page,
     and the element has to be back in the grid for the column to exist. */
  (OVERLAY.addEventListener ? OVERLAY.addEventListener.bind(OVERLAY, 'change') : OVERLAY.addListener.bind(OVERLAY))(function (event) {
    var d = document.querySelector('.fx-drawer');
    if (!event.matches) {
      unlockScroll();
      if (d && !d.hidden) {
        portalHome(d);
        /* WP4-K: it has just stopped being a sheet, so it has just stopped being modal. */
        setModal(d, false);
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }
    } else if (d && !d.hidden) {
      lockScroll();
      portalOut(d);
      setModal(d, true);
    }
  });

  /* WP4-K (2026-09-06). The dialog role and aria-modal describe the PHONE'S BOTTOM SHEET, and
     nothing else: a modal is a thing with a scrim that makes the rest of the page inert. From
     900px up the very same form is an inline column beside the results, so carrying the pair
     permanently told a screen reader the product wall was inert whenever the filters were open.
     Applied and removed alongside the scroll lock and the portal, which have exactly the same
     overlay-only condition. */
  function setModal(drawer, on) {
    if (!drawer) return;
    if (on) {
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
    } else {
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
    }
  }

  function openDrawer(trigger) {
    var id = trigger.getAttribute('aria-controls');
    var drawer = id ? document.getElementById(id) : null;
    if (!drawer) return;
    if (OVERLAY.matches) portalOut(drawer);
    setModal(drawer, OVERLAY.matches);
    drawer.hidden = false;
    setLabel(drawer, true);
    trigger.setAttribute('aria-expanded', 'true');
    if (typeof rememberOpen === 'function') rememberOpen(true);
    lockScroll();
    /* Only meaningful as a sheet; the header is display:none inline. A sheet
       is modal (it has a scrim), so it also contains focus (audit T-23). */
    if (OVERLAY.matches) {
      var close = drawer.querySelector('.fx-drawer__head [data-fx-drawer-close]');
      if (close) close.focus();
      if (typeof trapFocus === 'function') trapFocus(drawer, close || drawer);
    }
  }

  /* The button says Show filters / Hide filters, so its label has to follow the
     panel state (Andre, 2026-08-25). */
  /* EVERY [data-fx-label] span, not the first (2026-09-04). The trigger may now
     carry two: a long label for the desktop and a short one for the phone, with
     CSS showing one. Writing only the first left the other on its opening text
     for the rest of the visit, and it is the visible one at 393. Each span keeps
     its OWN show text, cached on itself the first time, because the trigger's
     copy would put the desktop wording into the phone span. A span may carry its
     own data-fx-hide-label; without one it falls back to the trigger's, which is
     what a single-span trigger has always done. */
  function setLabel(drawer, open) {
    var trigger = document.querySelector('[data-fx-drawer-open][aria-controls="' + drawer.id + '"]');
    if (!trigger) return;
    var spans = trigger.querySelectorAll('[data-fx-label]');
    if (!spans.length) return;
    for (var i = 0; i < spans.length; i++) {
      var span = spans[i];
      var show = span.getAttribute('data-fx-show-label');
      if (!show) { show = span.textContent; span.setAttribute('data-fx-show-label', show); }
      var hide = span.getAttribute('data-fx-hide-label') || trigger.getAttribute('data-fx-hide-label') || show;
      span.textContent = open ? hide : show;
    }
  }

  function closeDrawer(drawer) {
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    setModal(drawer, false);
    if (typeof rememberOpen === 'function') rememberOpen(false);
    portalHome(drawer);
    setLabel(drawer, false);
    unlockScroll();
    var trigger = document.querySelector('[data-fx-drawer-open][aria-controls="' + drawer.id + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(trigger || null);
    else if (trigger) trigger.focus();
  }

  function closeAllDrawers() {
    var drawers = document.querySelectorAll('.fx-drawer');
    for (var i = 0; i < drawers.length; i++) closeDrawer(drawers[i]);
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest ? event.target.closest('[data-fx-toggle]') : null;
    if (toggle) {
      var panel = panelFor(toggle);
      var wasOpen = toggle.getAttribute('aria-expanded') === 'true';
      closePanels(toggle);
      toggle.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
      toggle.classList.toggle('is-open', !wasOpen);
      if (panel) panel.hidden = wasOpen;
      return;
    }

    var open = event.target.closest ? event.target.closest('[data-fx-drawer-open]') : null;
    if (open) {
      /* The button is Show AND Hide (Andre, 2026-08-25): as a bottom sheet the
         sheet had its own close control, so this only ever opened. Inline on
         desktop the button is the only control, so it has to toggle. */
      var id = open.getAttribute('aria-controls');
      var target = id ? document.getElementById(id) : null;
      if (target && !target.hidden) closeDrawer(target);
      else openDrawer(open);
      return;
    }

    var close = event.target.closest ? event.target.closest('[data-fx-drawer-close]') : null;
    if (close) {
      closeDrawer(close.closest('.fx-drawer'));
      return;
    }

    // A click anywhere outside an open facet closes it.
    if (!event.target.closest || !event.target.closest('.fx-facet')) closePanels(null);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    closePanels(null);
    closeAllDrawers();
  });

  /* --- submit ------------------------------------------------------------- */

  /* THE DESKTOP COLUMN APPLIES ON CHANGE (Andre, 2026-09-05: "When I click
     colour, nothing changes. When I click material, nothing changes"). The
     stylesheet hides the sheet's Show button in column mode on the promise that
     "apply-on-change happens live in this mode", but only the old horizontal
     bar ever carried data-fx-autosubmit, so the column ticked the box and had
     no way to submit it. As a phone sheet the drawer still waits for Show. */
  document.addEventListener('change', function (event) {
    var form = event.target.form;
    if (!form) return;
    var live = form.hasAttribute('data-fx-autosubmit') || (form.classList.contains('fx-drawer') && !OVERLAY.matches);
    if (!live) return;
    form.submit();
  });

  /* A live column reloads the page on every tick, and the drawer is server
     rendered hidden, so without this the column would close on each filter and
     the customer would reopen it for the next. The flag lives for the tab. */
  var OPEN_KEY = 'pulse-fx-open';
  function rememberOpen(open) { try { if (open) sessionStorage.setItem(OPEN_KEY, '1'); else sessionStorage.removeItem(OPEN_KEY); } catch (e) { /* private mode */ } }
  (function reopenColumn() {
    if (OVERLAY.matches) return;
    var want = false;
    try { want = sessionStorage.getItem(OPEN_KEY) === '1'; } catch (e) { return; }
    if (!want) return;
    var trigger = document.querySelector('[data-fx-drawer-open]');
    if (trigger) openDrawer(trigger);
  })();

  /* --- the social clips ---------------------------------------------------- */

  var seen = typeof WeakSet === 'function' ? new WeakSet() : null;

  function watchVideos() {
    if (typeof IntersectionObserver !== 'function') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var videos = document.querySelectorAll('[data-pulse-feed-video]');
    if (!videos.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var video = entries[i].target;
          if (entries[i].isIntersecting) {
            var played = video.play();
            if (played && typeof played.catch === 'function') played.catch(function () {});
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.5 }
    );

    for (var i = 0; i < videos.length; i++) {
      if (seen) {
        if (seen.has(videos[i])) continue;
        seen.add(videos[i]);
      }
      observer.observe(videos[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchVideos);
  } else {
    watchVideos();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('.cx-wall')) watchVideos();
  });
})();
